import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Camera,
  FileXls,
  Sparkle,
  CheckCircle,
  ArrowRight,
  Lightning,
} from "@phosphor-icons/react";

/**
 * AiShowcase, zeigt die zwei "Magic"-Features auf der Landingpage:
 * 1) Foto vom Spritztagebuch -> automatisch erfasst (OCR)
 * 2) Excel-Liste hochladen -> KI mappt -> startklar (Import)
 *
 * Beide Demos sind animierte Loops (CSS + Framer Motion, kein Video).
 */
export const AiShowcase = () => {
  return (
    <section
      id="ki"
      data-testid="ai-showcase"
      className="relative py-24 sm:py-32 bg-gradient-to-b from-bone via-bone to-bone-100/40 overflow-hidden"
    >
      {/* subtile Hintergrund-Texturen */}
      <div className="absolute -top-32 -right-20 w-[40rem] h-[40rem] rounded-full bg-forest-100/40 blur-3xl pointer-events-none" aria-hidden="true" />
      <div className="absolute -bottom-40 -left-20 w-[36rem] h-[36rem] rounded-full bg-terracotta/10 blur-3xl pointer-events-none" aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl mb-14"
        >
          <div className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-terracotta mb-3">
            <Sparkle size={14} weight="fill" /> Mit KI
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-forest-900 tracking-tight">
            Statt eintippen. <span className="text-forest-600">einfach übernehmen.</span>
          </h2>
          <p className="mt-5 font-body text-base sm:text-lg text-[#4A524C]">
            Du hast Jahre an Spritztagebüchern auf Papier oder in Excel?
            Plantus liest sie für dich und legt deine Daten in unter einer Minute an.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8" data-testid="ai-showcase-grid">
          <OcrDemoCard />
          <ImportDemoCard />
        </div>

        {/* Vergleichszeile */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-12 max-w-4xl mx-auto"
          data-testid="ai-showcase-comparison"
        >
          <div className="grid sm:grid-cols-3 gap-3 sm:gap-4 text-center text-sm">
            <ComparisonStat label="Händisch" value="3-5 Stunden" tone="muted" sub="100 Anwendungen abtippen" />
            <ArrowConnector />
            <ComparisonStat label="Mit Plantus" value="unter 1 Minute" tone="hero" sub="KI macht den Rest" />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const ComparisonStat = ({ label, value, tone, sub }) => {
  const isHero = tone === "hero";
  return (
    <div
      className={`rounded-3xl p-5 sm:p-6 ${
        isHero
          ? "bg-forest-900 text-white shadow-lg ring-1 ring-forest-900/15"
          : "bg-white border border-bone-200 text-forest-900"
      }`}
    >
      <div
        className={`text-[10px] uppercase tracking-[0.2em] ${
          isHero ? "text-white/60" : "text-[#7A827C]"
        }`}
      >
        {label}
      </div>
      <div className="font-heading text-2xl sm:text-3xl font-semibold mt-1">{value}</div>
      <div className={`text-xs mt-1 ${isHero ? "text-white/70" : "text-[#4A524C]"}`}>{sub}</div>
    </div>
  );
};

const ArrowConnector = () => (
  <div className="hidden sm:flex items-center justify-center">
    <div className="flex items-center gap-2 text-terracotta">
      <span className="h-px w-12 bg-terracotta/40" aria-hidden="true" />
      <ArrowRight size={20} weight="bold" />
      <span className="h-px w-12 bg-terracotta/40" aria-hidden="true" />
    </div>
  </div>
);

/* -------------------- OCR Demo (animated loop) -------------------- */
const OCR_STEPS = [
  { id: 0, label: "Foto wird gelesen" },
  { id: 1, label: "Mittel: Karate Zeon" },
  { id: 2, label: "Menge: 0,075 l/ha" },
  { id: 3, label: "Wartezeit: 35 Tage" },
  { id: 4, label: "BVL-Treffer ✓" },
];

const OcrDemoCard = () => {
  const [stepIdx, setStepIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setStepIdx((i) => (i + 1) % (OCR_STEPS.length + 1)), 1300);
    return () => clearInterval(t);
  }, []);

  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6 }}
      data-testid="ai-showcase-ocr"
      className="relative overflow-hidden rounded-3xl bg-white border border-bone-200 shadow-[0_1px_2px_rgba(20,30,20,0.04),0_8px_30px_-14px_rgba(20,30,20,0.18)] p-6 sm:p-8"
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="w-11 h-11 rounded-2xl bg-forest-50 text-forest-700 flex items-center justify-center">
          <Camera size={20} weight="duotone" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-terracotta font-semibold">Foto-Erkennung</div>
          <h3 className="font-heading text-xl font-semibold text-forest-900">
            Spritztagebuch fotografieren
          </h3>
        </div>
      </div>

      <p className="text-sm text-[#4A524C] mb-6 max-w-md">
        Ein Foto deines handschriftlichen Heftes oder eines PSM-Etiketts genügt. die KI extrahiert Mittel, Menge, Datum, Wartezeit und ordnet alles direkt der BVL-Datenbank zu.
      </p>

      {/* Mock-Notizzettel + animierte Scanner-Linie */}
      <div className="relative bg-bone/50 border border-bone-200 rounded-2xl p-4 sm:p-5 overflow-hidden" aria-hidden="true">
        <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-[13px] font-mono text-forest-900/85 leading-relaxed">
          <span className="text-[#7A827C]">Datum:</span><span>02.06.2026 09:30</span>
          <span className="text-[#7A827C]">Schlag:</span><span>Acker Nord, 4,8 ha</span>
          <span className="text-[#7A827C]">Kultur:</span><span>Winterweizen</span>
          <span className="text-[#7A827C]">Mittel:</span><span>Karate Zeon</span>
          <span className="text-[#7A827C]">Menge:</span><span>0,075 l/ha</span>
          <span className="text-[#7A827C]">Wartezeit:</span><span>35 Tage</span>
        </div>

        {/* Scanner-Linie */}
        <motion.div
          aria-hidden="true"
          className="absolute inset-x-0 h-12 bg-gradient-to-b from-forest-400/0 via-forest-400/30 to-forest-400/0"
          initial={{ y: -40 }}
          animate={{ y: "120%" }}
          transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      {/* Erkannte Felder erscheinen nacheinander */}
      <div className="mt-5 space-y-2 min-h-[160px]" data-testid="ai-showcase-ocr-extracted">
        <AnimatePresence mode="popLayout">
          {OCR_STEPS.slice(0, stepIdx + 1).map((s) => (
            <motion.div
              key={s.id}
              initial={{ opacity: 0, x: -12 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35 }}
              className={`flex items-center gap-2 text-sm rounded-full px-3 py-1.5 w-fit ${
                s.id === 4
                  ? "bg-emerald-50 text-emerald-800 font-semibold"
                  : "bg-forest-50 text-forest-800"
              }`}
            >
              <CheckCircle size={14} weight="fill" className={s.id === 4 ? "text-emerald-600" : "text-forest-600"} />
              {s.label}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </motion.article>
  );
};

/* -------------------- Excel Import Demo -------------------- */
const ROWS = [
  { schlag: "Acker Nord",  groesse: "4.8", kultur: "Winterweizen",  eppo: "TRZAW" },
  { schlag: "Acker Süd",   groesse: "6.2", kultur: "Winterraps",    eppo: "BRSNN" },
  { schlag: "Wiese West",  groesse: "2.5", kultur: "Grünland",      eppo: ","      },
];
const COLS = [
  { src: "Schlag",       target: "Flächenname",  conf: 99 },
  { src: "Groesse (ha)", target: "Größe (ha)",    conf: 99 },
  { src: "Kultur",       target: "Kultur",        conf: 95 },
  { src: "EPPO",         target: "EPPO-Code",     conf: 98 },
];

const ImportDemoCard = () => {
  const [activeCol, setActiveCol] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setActiveCol((i) => (i + 1) % (COLS.length + 1)), 1400);
    return () => clearInterval(t);
  }, []);

  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: 0.1 }}
      data-testid="ai-showcase-import"
      className="relative overflow-hidden rounded-3xl bg-forest-900 text-white border border-forest-900/20 shadow-[0_1px_2px_rgba(20,30,20,0.04),0_8px_30px_-14px_rgba(20,30,20,0.18)] p-6 sm:p-8"
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="w-11 h-11 rounded-2xl bg-white/12 backdrop-blur ring-1 ring-white/15 flex items-center justify-center">
          <FileXls size={20} weight="duotone" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-terracotta font-semibold">Excel-Import</div>
          <h3 className="font-heading text-xl font-semibold">
            Bestehende Liste hochladen
          </h3>
        </div>
      </div>

      <p className="text-sm text-white/75 mb-6 max-w-md">
        Lade deine Excel- oder CSV-Liste hoch. die KI erkennt automatisch, welche Spalte was bedeutet, und legt deine Flächen oder Anwendungen für dich an.
      </p>

      {/* Mock-Excel + Mapping-Animation */}
      <div className="rounded-2xl bg-white/[0.06] backdrop-blur ring-1 ring-white/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-white/[0.07]">
              <tr>
                {COLS.map((c, i) => (
                  <th
                    key={c.src}
                    className={`text-left px-3 py-2 font-semibold transition-colors ${
                      i === activeCol ? "text-emerald-300" : "text-white/80"
                    }`}
                  >
                    {c.src}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROWS.map((r, ri) => (
                <tr key={ri} className="border-t border-white/5">
                  {[r.schlag, r.groesse, r.kultur, r.eppo].map((v, ci) => (
                    <td
                      key={ci}
                      className={`px-3 py-2 transition-colors ${
                        ci === activeCol ? "text-emerald-200" : "text-white/70"
                      }`}
                    >
                      {v}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mapping-Pfeile */}
        <div className="px-3 py-3 border-t border-white/10 bg-white/[0.03] text-[11px] font-mono text-white/80">
          <AnimatePresence mode="popLayout">
            {COLS.slice(0, Math.min(activeCol + 1, COLS.length)).map((c) => (
              <motion.div
                key={c.src}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="flex items-center gap-2 py-0.5"
              >
                <span className="text-white/60">{c.src}</span>
                <ArrowRight size={12} weight="bold" className="text-emerald-300" />
                <span className="text-emerald-200 font-semibold">{c.target}</span>
                <span className="ml-auto text-emerald-300/80">{c.conf}%</span>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>

      <div className="mt-5 flex items-center justify-between text-sm">
        <div className="inline-flex items-center gap-2 text-emerald-200">
          <Lightning size={16} weight="fill" />
          <span className="font-semibold">3 Flächen erkannt · 0 Fehler</span>
        </div>
        <a
          href="/register"
          data-testid="ai-showcase-cta"
          className="inline-flex items-center gap-1.5 bg-white text-forest-900 hover:bg-bone font-semibold text-xs px-3.5 py-2 rounded-full transition-all"
        >
          Jetzt ausprobieren <ArrowRight size={12} weight="bold" />
        </a>
      </div>
    </motion.article>
  );
};
